import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Sparkles, Download, Loader2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LessonPlanner() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);

  const [formData, setFormData] = useState({
    curriculum: 'Cambridge Primary',
    lessonFormat: '5E Lesson Plan',
    grade: 'Year 4',
    subject: 'Mathematics',
    topic: '',
    unit: '',
    duration: '45',
    numberOfLessons: '1',
    objectives: '',
    priorKnowledge: '',
    classAbility: 'Mixed',
    resources: '',
    setting: 'Classroom',
    workType: 'Mixed',
    homework: 'Yes',
    differentiation: 'Yes',
    assessment: 'Yes',
    reflection: 'Yes',
    differentiationLevel: 'Mixed-ability classroom'
  });

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      
      if (response.data.subscription_tier === 'free') {
        toast.error('AI Lesson Planner requires Teacher Plus or Teacher Pro membership');
        navigate('/pricing');
      }
    } catch (error) {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const lessonFormats = {
    '5E Lesson Plan': ['Engage', 'Explore', 'Explain', 'Elaborate', 'Evaluate'],
    'Three-Part Lesson Plan': ['Introduction / Starter', 'Main Teaching and Learning', 'Plenary / Reflection'],
    'Cambridge Active Learning Lesson Plan': [
      'Curriculum framework reference',
      'Learning objectives',
      'Success criteria',
      'Prior knowledge',
      'Starter activity',
      'Teacher-led input',
      'Active learning activities',
      'Guided practice',
      'Independent learning',
      'Differentiation',
      'Assessment for learning',
      'Plenary',
      'Homework',
      'Teacher reflection'
    ],
    'Cambridge Short-Term Lesson Plan': [
      'Stage / Year',
      'Learning objectives',
      'Success criteria',
      'Vocabulary',
      'Resources',
      'Time allocation',
      'Teaching activities',
      'Differentiation',
      'Assessment opportunities',
      'Plenary',
      'Reflection'
    ],
    'Weekly Cambridge Lesson Plan': [
      'Day / Lesson',
      'Learning objective',
      'Success criteria',
      'Starter',
      'Main activity',
      'Assessment',
      'Differentiation',
      'Resources',
      'Homework',
      'Reflection'
    ],
    'Direct Instruction Lesson Plan': [
      'Learning objective',
      'Review prior learning',
      'Teacher modelling',
      'Guided practice',
      'Independent practice',
      'Assessment',
      'Plenary',
      'Homework'
    ],
    'Inquiry-Based Lesson Plan': [
      'Inquiry question',
      'Prior knowledge',
      'Engage',
      'Investigate',
      'Discuss findings',
      'Apply learning',
      'Assessment',
      'Reflection'
    ],
    'Project-Based Lesson Plan': [
      'Driving question',
      'Learning goals',
      'Project overview',
      'Materials',
      'Project stages',
      'Collaboration roles',
      'Checkpoints',
      'Assessment rubric',
      'Presentation',
      'Reflection'
    ]
  };

  const handleGenerate = async () => {
    if (!formData.topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    if (!formData.objectives.trim()) {
      toast.error('Please enter learning objectives');
      return;
    }

    setGenerating(true);

    try {
      const formatSections = lessonFormats[formData.lessonFormat].join(', ');
      
      const prompt = `Generate a comprehensive ${formData.lessonFormat} for ${formData.curriculum}.

**Basic Information:**
- Grade: ${formData.grade}
- Subject: ${formData.subject}
- Topic: ${formData.topic}
- Unit: ${formData.unit || 'Not specified'}
- Duration: ${formData.duration} minutes
- Number of Lessons: ${formData.numberOfLessons}

**Learning Objectives:**
${formData.objectives}

**Prior Knowledge:**
${formData.priorKnowledge || 'Students have basic understanding of the topic'}

**Class Details:**
- Ability Level: ${formData.classAbility}
- Setting: ${formData.setting}
- Work Type: ${formData.workType}
- Resources Available: ${formData.resources || 'Standard classroom resources'}

**Required Sections:**
${formatSections}

**Additional Requirements:**
${formData.differentiation === 'Yes' ? `- Include differentiation strategies (${formData.differentiationLevel})` : ''}
${formData.assessment === 'Yes' ? '- Include assessment for learning opportunities' : ''}
${formData.homework === 'Yes' ? '- Include relevant homework tasks' : ''}
${formData.reflection === 'Yes' ? '- Include teacher reflection section' : ''}

Please create a detailed, practical lesson plan that:
1. Follows the ${formData.lessonFormat} structure exactly
2. Provides specific, actionable activities
3. Includes timing for each section
4. Offers practical differentiation (not generic statements)
5. Is ready to use in the classroom
6. Aligns with ${formData.curriculum} standards

Format the plan professionally with clear headings and bullet points.`;

      const response = await axios.post(
        `${API}/ai/generate-lesson`,
        { 
          prompt, 
          grade: formData.grade, 
          subject: formData.subject,
          lesson_format: formData.lessonFormat
        },
        { withCredentials: true }
      );

      setGeneratedContent(response.data.content);
      toast.success('Lesson plan generated successfully!');
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate lesson plan');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPDF = () => {
    if (!generatedContent) return;
    
    // Build HTML content directly from the data structure
    let contentHTML = '';
    
    // Header section
    if (generatedContent.header) {
      contentHTML += `<div style="margin-bottom: 20px; padding-bottom: 20px; border-bottom: 2px solid #e2e8f0;">`;
      contentHTML += `<h2>${generatedContent.header.title || generatedContent.title}</h2>`;
      if (generatedContent.header.subtitle) {
        contentHTML += `<p style="color: #64748b;">${generatedContent.header.subtitle}</p>`;
      }
      contentHTML += `</div>`;
    }
    
    // Learning Objectives
    if (generatedContent.learning_objectives && generatedContent.learning_objectives.length > 0) {
      contentHTML += `<div style="margin-bottom: 20px;">`;
      contentHTML += `<h3 style="color: #1e293b; margin-bottom: 10px;">Learning Objectives:</h3>`;
      contentHTML += `<ul style="margin-left: 20px;">`;
      generatedContent.learning_objectives.forEach(obj => {
        const text = typeof obj === 'object' ? (obj.text || obj.objective) : obj;
        contentHTML += `<li style="margin-bottom: 5px;">${text}</li>`;
      });
      contentHTML += `</ul></div>`;
    }
    
    // Sections (main lesson content)
    if (generatedContent.sections && generatedContent.sections.length > 0) {
      generatedContent.sections.forEach(section => {
        contentHTML += `<div style="margin-bottom: 25px; padding-bottom: 20px; border-bottom: 1px solid #e2e8f0;">`;
        contentHTML += `<h3 style="color: #1e40af; margin-bottom: 10px;">📖 ${section.title}</h3>`;
        
        if (section.timing) {
          contentHTML += `<p style="font-size: 14px; color: #64748b; margin-bottom: 10px;"><strong>Time:</strong> ${section.timing}</p>`;
        }
        
        if (section.description) {
          contentHTML += `<p style="margin-bottom: 10px;">${section.description}</p>`;
        }
        
        if (section.activities && section.activities.length > 0) {
          contentHTML += `<p style="font-weight: 600; margin-top: 10px;">Activities:</p>`;
          contentHTML += `<ul style="margin-left: 20px;">`;
          section.activities.forEach(activity => {
            const text = typeof activity === 'object' ? (activity.text || activity.description) : activity;
            contentHTML += `<li style="margin-bottom: 5px;">${text}</li>`;
          });
          contentHTML += `</ul>`;
        }
        
        if (section.resources && section.resources.length > 0) {
          contentHTML += `<p style="font-weight: 600; margin-top: 10px;">Resources:</p>`;
          contentHTML += `<ul style="margin-left: 20px;">`;
          section.resources.forEach(resource => {
            const text = typeof resource === 'object' ? (resource.text || resource.name) : resource;
            contentHTML += `<li style="margin-bottom: 5px;">${text}</li>`;
          });
          contentHTML += `</ul>`;
        }
        
        contentHTML += `</div>`;
      });
    }
    
    // Differentiation
    if (generatedContent.differentiation) {
      contentHTML += `<div style="background-color: #eff6ff; padding: 15px; border-radius: 8px; margin: 15px 0;">`;
      contentHTML += `<h3 style="color: #1e293b; margin-bottom: 10px;">Differentiation:</h3>`;
      if (typeof generatedContent.differentiation === 'string') {
        contentHTML += `<p>${generatedContent.differentiation}</p>`;
      } else if (Array.isArray(generatedContent.differentiation)) {
        contentHTML += `<ul style="margin-left: 20px;">`;
        generatedContent.differentiation.forEach(item => {
          contentHTML += `<li style="margin-bottom: 5px;">${item}</li>`;
        });
        contentHTML += `</ul>`;
      }
      contentHTML += `</div>`;
    }
    
    // Assessment
    if (generatedContent.assessment) {
      contentHTML += `<div style="background-color: #f0fdf4; padding: 15px; border-radius: 8px; margin: 15px 0;">`;
      contentHTML += `<h3 style="color: #1e293b; margin-bottom: 10px;">Assessment:</h3>`;
      if (typeof generatedContent.assessment === 'string') {
        contentHTML += `<p>${generatedContent.assessment}</p>`;
      } else if (Array.isArray(generatedContent.assessment)) {
        contentHTML += `<ul style="margin-left: 20px;">`;
        generatedContent.assessment.forEach(item => {
          contentHTML += `<li style="margin-bottom: 5px;">${item}</li>`;
        });
        contentHTML += `</ul>`;
      }
      contentHTML += `</div>`;
    }
    
    // Create printable version
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow pop-ups to download the lesson plan');
      return;
    }
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Lesson Plan - ${formData.topic}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; line-height: 1.6; }
            h1 { color: #1e293b; border-bottom: 3px solid #3b82f6; padding-bottom: 10px; }
            h2 { color: #1e293b; margin-top: 20px; }
            h3 { color: #334155; margin-top: 15px; }
            ul { margin-left: 20px; line-height: 1.8; }
            li { margin-bottom: 8px; }
            @media print {
              body { padding: 10px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>Lesson Plan: ${formData.topic}</h1>
          <p><strong>Subject:</strong> ${formData.subject} | <strong>Grade:</strong> ${formData.grade} | <strong>Duration:</strong> ${formData.duration} minutes</p>
          <hr />
          ${contentHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleDownloadWord = () => {
    if (!generatedContent) return;
    
    // Build HTML content directly from the data structure
    let contentHTML = '';
    
    // Header section
    if (generatedContent.header) {
      contentHTML += `<div style="margin-bottom: 20px; padding-bottom: 20px; border-bottom: 2px solid #e2e8f0;">`;
      contentHTML += `<h2>${generatedContent.header.title || generatedContent.title}</h2>`;
      if (generatedContent.header.subtitle) {
        contentHTML += `<p style="color: #64748b;">${generatedContent.header.subtitle}</p>`;
      }
      contentHTML += `</div>`;
    }
    
    // Learning Objectives
    if (generatedContent.learning_objectives && generatedContent.learning_objectives.length > 0) {
      contentHTML += `<div style="margin-bottom: 20px;">`;
      contentHTML += `<h3 style="color: #1e293b;">Learning Objectives:</h3>`;
      contentHTML += `<ul>`;
      generatedContent.learning_objectives.forEach(obj => {
        const text = typeof obj === 'object' ? (obj.text || obj.objective) : obj;
        contentHTML += `<li>${text}</li>`;
      });
      contentHTML += `</ul></div>`;
    }
    
    // Sections (main lesson content)
    if (generatedContent.sections && generatedContent.sections.length > 0) {
      generatedContent.sections.forEach(section => {
        contentHTML += `<div style="margin-bottom: 25px;">`;
        contentHTML += `<h3 style="color: #1e40af;">${section.title}</h3>`;
        
        if (section.timing) {
          contentHTML += `<p><strong>Time:</strong> ${section.timing}</p>`;
        }
        
        if (section.description) {
          contentHTML += `<p>${section.description}</p>`;
        }
        
        if (section.activities && section.activities.length > 0) {
          contentHTML += `<p><strong>Activities:</strong></p><ul>`;
          section.activities.forEach(activity => {
            const text = typeof activity === 'object' ? (activity.text || activity.description) : activity;
            contentHTML += `<li>${text}</li>`;
          });
          contentHTML += `</ul>`;
        }
        
        if (section.resources && section.resources.length > 0) {
          contentHTML += `<p><strong>Resources:</strong></p><ul>`;
          section.resources.forEach(resource => {
            const text = typeof resource === 'object' ? (resource.text || resource.name) : resource;
            contentHTML += `<li>${text}</li>`;
          });
          contentHTML += `</ul>`;
        }
        
        contentHTML += `</div>`;
      });
    }
    
    // Differentiation
    if (generatedContent.differentiation) {
      contentHTML += `<div style="background-color: #eff6ff; padding: 15px; margin: 15px 0;">`;
      contentHTML += `<h3>Differentiation:</h3>`;
      if (typeof generatedContent.differentiation === 'string') {
        contentHTML += `<p>${generatedContent.differentiation}</p>`;
      } else if (Array.isArray(generatedContent.differentiation)) {
        contentHTML += `<ul>`;
        generatedContent.differentiation.forEach(item => {
          contentHTML += `<li>${item}</li>`;
        });
        contentHTML += `</ul>`;
      }
      contentHTML += `</div>`;
    }
    
    // Assessment
    if (generatedContent.assessment) {
      contentHTML += `<div style="background-color: #f0fdf4; padding: 15px; margin: 15px 0;">`;
      contentHTML += `<h3>Assessment:</h3>`;
      if (typeof generatedContent.assessment === 'string') {
        contentHTML += `<p>${generatedContent.assessment}</p>`;
      } else if (Array.isArray(generatedContent.assessment)) {
        contentHTML += `<ul>`;
        generatedContent.assessment.forEach(item => {
          contentHTML += `<li>${item}</li>`;
        });
        contentHTML += `</ul>`;
      }
      contentHTML += `</div>`;
    }
    
    // Create HTML content for Word download
    const htmlContent = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'>
        <head><meta charset='utf-8'><title>Lesson Plan - ${formData.topic}</title></head>
        <body>
          <h1>Lesson Plan: ${formData.topic}</h1>
          <p><strong>Subject:</strong> ${formData.subject} | <strong>Grade:</strong> ${formData.grade} | <strong>Duration:</strong> ${formData.duration} minutes</p>
          <hr />
          ${contentHTML}
        </body>
      </html>
    `;
    
    const blob = new Blob([htmlContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lesson-plan-${formData.subject}-${formData.topic.replace(/\s+/g, '-').toLowerCase()}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Lesson plan downloaded!');
  };

  if (generatedContent) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <section className="py-16 px-6 sm:px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-3xl font-bold text-slate-900 mb-8">Your Generated Lesson Plan</h1>
            <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm mb-6 lesson-plan-content">
              {/* Render structured lesson plan content */}
              {generatedContent.header && (
                <div className="mb-6 pb-6 border-b">
                  <h2 className="text-2xl font-bold text-slate-900">{generatedContent.header.title || generatedContent.title}</h2>
                  {generatedContent.header.subtitle && (
                    <p className="text-slate-600 mt-1">{generatedContent.header.subtitle}</p>
                  )}
                  {(generatedContent.grade || generatedContent.subject || generatedContent.duration) && (
                    <div className="mt-3 flex flex-wrap gap-3 text-sm">
                      {generatedContent.grade && <span className="text-slate-600"><strong>Grade:</strong> {generatedContent.grade}</span>}
                      {generatedContent.subject && <span className="text-slate-600"><strong>Subject:</strong> {generatedContent.subject}</span>}
                      {generatedContent.duration && <span className="text-slate-600"><strong>Duration:</strong> {generatedContent.duration}</span>}
                    </div>
                  )}
                </div>
              )}

              {generatedContent.learning_objectives && generatedContent.learning_objectives.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-bold text-lg text-slate-900 mb-2">Learning Objectives:</h3>
                  <ul className="list-disc list-inside space-y-1 text-slate-700">
                    {generatedContent.learning_objectives.map((obj, i) => (
                      <li key={i}>{typeof obj === 'string' ? obj : obj.text || obj.objective || JSON.stringify(obj)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {generatedContent.sections && generatedContent.sections.map((section, idx) => (
                <div key={idx} className="mb-6 pb-6 border-b last:border-b-0">
                  <h3 className="font-bold text-lg text-blue-900 mb-3 flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    {section.title}
                  </h3>
                  {section.timing && (
                    <p className="text-sm text-slate-600 mb-2">
                      <strong>Time:</strong> {section.timing}
                    </p>
                  )}
                  {section.description && (
                    <p className="text-slate-700 mb-3">{section.description}</p>
                  )}
                  {section.activities && section.activities.length > 0 && (
                    <div className="mb-3">
                      <p className="font-semibold text-slate-800 mb-1">Activities:</p>
                      <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                        {section.activities.map((activity, i) => (
                          <li key={i}>{typeof activity === 'string' ? activity : activity.text || activity.description || JSON.stringify(activity)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {section.resources && section.resources.length > 0 && (
                    <div className="mb-3">
                      <p className="font-semibold text-slate-800 mb-1">Resources:</p>
                      <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                        {section.resources.map((resource, i) => (
                          <li key={i}>{typeof resource === 'string' ? resource : resource.text || resource.name || JSON.stringify(resource)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}

              {generatedContent.differentiation && (
                <div className="mb-6 bg-blue-50 rounded-lg p-4">
                  <h3 className="font-bold text-lg text-slate-900 mb-2">Differentiation:</h3>
                  {typeof generatedContent.differentiation === 'string' ? (
                    <p className="text-slate-700">{generatedContent.differentiation}</p>
                  ) : Array.isArray(generatedContent.differentiation) ? (
                    <ul className="list-disc list-inside space-y-1 text-slate-700">
                      {generatedContent.differentiation.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              )}

              {generatedContent.assessment && (
                <div className="mb-6 bg-green-50 rounded-lg p-4">
                  <h3 className="font-bold text-lg text-slate-900 mb-2">Assessment:</h3>
                  {typeof generatedContent.assessment === 'string' ? (
                    <p className="text-slate-700">{generatedContent.assessment}</p>
                  ) : Array.isArray(generatedContent.assessment) ? (
                    <ul className="list-disc list-inside space-y-1 text-slate-700">
                      {generatedContent.assessment.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              )}

              {/* Fallback for plain text content */}
              {!generatedContent.sections && typeof generatedContent === 'string' && (
                <div className="prose max-w-none whitespace-pre-wrap">{generatedContent}</div>
              )}
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setGeneratedContent(null)}
                className="bg-slate-100 text-slate-700 hover:bg-slate-200 px-6 py-3 rounded-xl font-semibold"
              >
                Generate Another
              </button>
              <button className="bg-blue-600 text-white hover:bg-blue-700 px-6 py-3 rounded-xl font-semibold">
                Edit Plan
              </button>
              <button 
                onClick={handleDownloadWord}
                className="bg-purple-600 text-white hover:bg-purple-700 px-6 py-3 rounded-xl font-semibold inline-flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                Download Word
              </button>
              <button 
                onClick={handleDownloadPDF}
                className="bg-green-600 text-white hover:bg-green-700 px-6 py-3 rounded-xl font-semibold inline-flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                Download PDF
              </button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <BookOpen className="w-10 h-10 text-blue-600" />
              <h1 className="text-4xl font-bold text-slate-900">AI Lesson Planner</h1>
            </div>
            <p className="text-base text-slate-600">Generate complete lesson plans with objectives, activities, assessment and differentiation</p>
            <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
              <Sparkles className="w-4 h-4" />
              Teacher Plus Feature
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm space-y-8">
            {/* Curriculum & Format */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Curriculum *</label>
                <select
                  value={formData.curriculum}
                  onChange={(e) => setFormData({ ...formData, curriculum: e.target.value })}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
                >
                  <option>Cambridge Primary</option>
                  <option>Cambridge Lower Secondary</option>
                  <option>Cambridge IGCSE</option>
                  <option>IB</option>
                  <option>Oxford</option>
                  <option>National Curriculum</option>
                  <option>Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Lesson Plan Format *</label>
                <select
                  value={formData.lessonFormat}
                  onChange={(e) => setFormData({ ...formData, lessonFormat: e.target.value })}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
                >
                  {Object.keys(lessonFormats).map(format => (
                    <option key={format}>{format}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Format Preview */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-blue-900 mb-2">Selected Format Sections:</p>
              <div className="flex flex-wrap gap-2">
                {lessonFormats[formData.lessonFormat].map(section => (
                  <span key={section} className="px-3 py-1 bg-white border border-blue-200 rounded-full text-xs text-blue-800">
                    {section}
                  </span>
                ))}
              </div>
            </div>

            {/* Basic Details */}
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Grade *</label>
                <select
                  value={formData.grade}
                  onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
                >
                  <option value="3">Grade 3</option>
                  <option value="4">Grade 4</option>
                  <option value="5">Grade 5</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Subject *</label>
                <select
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
                >
                  <option>Mathematics</option>
                  <option>Science</option>
                  <option>English</option>
                  <option>Social Studies</option>
                  <option>Art</option>
                  <option>Physical Education</option>
                  <option>General</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Duration (min) *</label>
                <input
                  type="number"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
                />
              </div>
            </div>

            {/* Topic & Unit */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Topic *</label>
                <input
                  type="text"
                  value={formData.topic}
                  onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                  placeholder="e.g., Introduction to Fractions"
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Unit (Optional)</label>
                <input
                  type="text"
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  placeholder="e.g., Numbers and Operations"
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
                />
              </div>
            </div>

            {/* Learning Objectives */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Learning Objectives *</label>
              <textarea
                value={formData.objectives}
                onChange={(e) => setFormData({ ...formData, objectives: e.target.value })}
                placeholder="Enter 2-3 learning objectives..."
                rows={3}
                className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </div>

            {/* Prior Knowledge */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Prior Knowledge</label>
              <textarea
                value={formData.priorKnowledge}
                onChange={(e) => setFormData({ ...formData, priorKnowledge: e.target.value })}
                placeholder="What should students already know?"
                rows={2}
                className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </div>

            {/* Class Details */}
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Class Ability</label>
                <select
                  value={formData.classAbility}
                  onChange={(e) => setFormData({ ...formData, classAbility: e.target.value })}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
                >
                  <option>Mixed</option>
                  <option>High Achievers</option>
                  <option>Support Needed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Setting</label>
                <select
                  value={formData.setting}
                  onChange={(e) => setFormData({ ...formData, setting: e.target.value })}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
                >
                  <option>Classroom</option>
                  <option>Online</option>
                  <option>Hybrid</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Work Type</label>
                <select
                  value={formData.workType}
                  onChange={(e) => setFormData({ ...formData, workType: e.target.value })}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
                >
                  <option>Mixed</option>
                  <option>Individual</option>
                  <option>Pair Work</option>
                  <option>Group Work</option>
                </select>
              </div>
            </div>

            {/* Resources */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Available Resources</label>
              <input
                type="text"
                value={formData.resources}
                onChange={(e) => setFormData({ ...formData, resources: e.target.value })}
                placeholder="e.g., Whiteboard, manipulatives, tablets"
                className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </div>

            {/* Differentiation */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Differentiation</label>
              <select
                value={formData.differentiationLevel}
                onChange={(e) => setFormData({ ...formData, differentiationLevel: e.target.value })}
                className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              >
                <option>Support for struggling learners</option>
                <option>Core activity</option>
                <option>Extension for advanced learners</option>
                <option>English-language learner support</option>
                <option>Additional learning needs</option>
                <option>Mixed-ability classroom</option>
              </select>
            </div>

            {/* Options */}
            <div className="grid md:grid-cols-4 gap-6">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="homework"
                  checked={formData.homework === 'Yes'}
                  onChange={(e) => setFormData({ ...formData, homework: e.target.checked ? 'Yes' : 'No' })}
                  className="w-5 h-5"
                />
                <label htmlFor="homework" className="text-sm font-medium text-slate-700">Include Homework</label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="differentiation"
                  checked={formData.differentiation === 'Yes'}
                  onChange={(e) => setFormData({ ...formData, differentiation: e.target.checked ? 'Yes' : 'No' })}
                  className="w-5 h-5"
                />
                <label htmlFor="differentiation" className="text-sm font-medium text-slate-700">Include Differentiation</label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="assessment"
                  checked={formData.assessment === 'Yes'}
                  onChange={(e) => setFormData({ ...formData, assessment: e.target.checked ? 'Yes' : 'No' })}
                  className="w-5 h-5"
                />
                <label htmlFor="assessment" className="text-sm font-medium text-slate-700">Include Assessment</label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="reflection"
                  checked={formData.reflection === 'Yes'}
                  onChange={(e) => setFormData({ ...formData, reflection: e.target.checked ? 'Yes' : 'No' })}
                  className="w-5 h-5"
                />
                <label htmlFor="reflection" className="text-sm font-medium text-slate-700">Include Reflection</label>
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={generating}
              data-testid="generate-lesson-button"
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 px-6 py-4 rounded-xl font-semibold text-lg inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating Lesson Plan...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Generate Lesson Plan
                </>
              )}
            </button>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default LessonPlanner;
